import { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import { AppShell } from '../components/layout/AppShell';
import { Spinner } from '../components/ui/Spinner';
import { InternRoute, ProtectedRoute, PublicOnlyRoute, StaffRoute } from './guards';

/**
 * Every page is code-split (spec section 85). Admin screens in particular are
 * dead weight for an intern's bundle, so nothing outside the shell is eager.
 */

/* Public */
const LoginPage = lazy(() => import('../pages/LoginPage'));
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'));

/* Intern — learning */
const InternDashboardPage = lazy(() => import('../pages/intern/InternDashboardPage'));
const CourseCatalogPage = lazy(() => import('../pages/intern/CourseCatalogPage'));
const CourseDetailPage = lazy(() => import('../pages/intern/CourseDetailPage'));
const RecapPage = lazy(() => import('../pages/intern/RecapPage'));
const LessonPage = lazy(() => import('../pages/intern/LessonPage'));
const QuizPage = lazy(() => import('../pages/intern/QuizPage'));
const SkipAssessmentPage = lazy(() => import('../pages/intern/SkipAssessmentPage'));
const MyProgressPage = lazy(() => import('../pages/intern/MyProgressPage'));
const LearningHistoryPage = lazy(() => import('../pages/intern/LearningHistoryPage'));

/* Intern — practice */
const ExercisesPage = lazy(() => import('../pages/intern/ExercisesPage'));
const ExerciseDetailPage = lazy(() => import('../pages/intern/ExerciseDetailPage'));
const MySubmissionsPage = lazy(() => import('../pages/intern/MySubmissionsPage'));
const SubmissionDetailPage = lazy(() => import('../pages/intern/SubmissionDetailPage'));
const AssessmentsPage = lazy(() => import('../pages/intern/AssessmentsPage'));
const AssessmentAttemptPage = lazy(() => import('../pages/intern/AssessmentAttemptPage'));

/* Intern — growth */
const MySkillsPage = lazy(() => import('../pages/intern/MySkillsPage'));
const AchievementsPage = lazy(() => import('../pages/intern/AchievementsPage'));
const ReadinessPage = lazy(() => import('../pages/intern/ReadinessPage'));

/* Intern — project work */
const MyProjectsPage = lazy(() => import('../pages/intern/MyProjectsPage'));
const MyTasksPage = lazy(() => import('../pages/intern/MyTasksPage'));
const TaskDetailPage = lazy(() => import('../pages/intern/TaskDetailPage'));

/* Shared */
const HandbookPage = lazy(() => import('../pages/HandbookPage'));
const NotificationsPage = lazy(() => import('../pages/NotificationsPage'));
const ProfilePage = lazy(() => import('../pages/ProfilePage'));
const SettingsPage = lazy(() => import('../pages/SettingsPage'));

/* Admin — overview and interns */
const AdminDashboardPage = lazy(() => import('../pages/admin/AdminDashboardPage'));
const InternsPage = lazy(() => import('../pages/admin/InternsPage'));
const InternDetailPage = lazy(() => import('../pages/admin/InternDetailPage'));

/* Admin — content */
const CourseManagerPage = lazy(() => import('../pages/admin/CourseManagerPage'));
const CourseBuilderPage = lazy(() => import('../pages/admin/CourseBuilderPage'));
const LessonEditorPage = lazy(() => import('../pages/admin/LessonEditorPage'));
const LearningPathsPage = lazy(() => import('../pages/admin/LearningPathsPage'));
const AssessmentManagerPage = lazy(() => import('../pages/admin/AssessmentManagerPage'));
const SkillsAdminPage = lazy(() => import('../pages/admin/SkillsAdminPage'));

/* Admin — review */
const ReviewQueuePage = lazy(() => import('../pages/admin/ReviewQueuePage'));
const ReviewDetailPage = lazy(() => import('../pages/admin/ReviewDetailPage'));
const TaskReviewQueuePage = lazy(() => import('../pages/admin/TaskReviewQueuePage'));
const TaskReviewDetailPage = lazy(() => import('../pages/admin/TaskReviewDetailPage'));
const SkipRequestsPage = lazy(() => import('../pages/admin/SkipRequestsPage'));

/* Admin — insight */
const ReportsPage = lazy(() => import('../pages/admin/ReportsPage'));
const ReportViewerPage = lazy(() => import('../pages/admin/ReportViewerPage'));
const SkipReportPage = lazy(() => import('../pages/admin/SkipReportPage'));
const AnalyticsPage = lazy(() => import('../pages/admin/AnalyticsPage'));
const ActivityLogPage = lazy(() => import('../pages/admin/ActivityLogPage'));
const AuditLogPage = lazy(() => import('../pages/admin/AuditLogPage'));

/* Admin — projects */
const ProjectsAdminPage = lazy(() => import('../pages/admin/ProjectsAdminPage'));
const ProjectDetailPage = lazy(() => import('../pages/admin/ProjectDetailPage'));
const TasksAdminPage = lazy(() => import('../pages/admin/TasksAdminPage'));

/* Admin — business */
const ClientsPage = lazy(() => import('../pages/admin/ClientsPage'));
const ClientDetailPage = lazy(() => import('../pages/admin/ClientDetailPage'));
const FinanceOverviewPage = lazy(() => import('../pages/admin/FinanceOverviewPage'));
const InvoicesPage = lazy(() => import('../pages/admin/InvoicesPage'));
const InvoiceDetailPage = lazy(() => import('../pages/admin/InvoiceDetailPage'));
const AccountantDashboardPage = lazy(() => import('../pages/admin/AccountantDashboardPage'));

/* Admin — platform */
const UsersPage = lazy(() => import('../pages/admin/UsersPage'));
const PlatformSettingsPage = lazy(() => import('../pages/admin/PlatformSettingsPage'));

function RouteFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center" role="status" aria-live="polite">
      <Spinner size="lg" />
      <span className="sr-only">Loading page…</span>
    </div>
  );
}

export function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route element={<PublicOnlyRoute />}>
          <Route path="/login" element={<LoginPage />} />
        </Route>

        <Route element={<ProtectedRoute />}>
          <Route element={<AppShell />}>
            {/* Learner surfaces */}
            <Route element={<InternRoute />}>
              <Route index element={<InternDashboardPage />} />
              <Route path="courses" element={<CourseCatalogPage />} />
              <Route path="courses/:slug" element={<CourseDetailPage />} />
              <Route path="courses/:slug/recap" element={<RecapPage />} />
              <Route path="lessons/:lessonId" element={<LessonPage />} />
              <Route path="lessons/:lessonId/quiz" element={<QuizPage />} />
              <Route path="lessons/:lessonId/skip" element={<SkipAssessmentPage />} />
              <Route path="progress" element={<MyProgressPage />} />
              <Route path="history" element={<LearningHistoryPage />} />
              <Route path="exercises" element={<ExercisesPage />} />
              <Route path="exercises/:exerciseId" element={<ExerciseDetailPage />} />
              <Route path="submissions" element={<MySubmissionsPage />} />
              <Route path="submissions/:submissionId" element={<SubmissionDetailPage />} />
              <Route path="assessments" element={<AssessmentsPage />} />
              <Route path="assessments/:assessmentId" element={<AssessmentAttemptPage />} />
              <Route path="skills" element={<MySkillsPage />} />
              <Route path="achievements" element={<AchievementsPage />} />
              <Route path="readiness" element={<ReadinessPage />} />
              <Route path="projects" element={<MyProjectsPage />} />
              <Route path="tasks" element={<MyTasksPage />} />
              <Route path="tasks/:taskId" element={<TaskDetailPage />} />
            </Route>

            {/* Shared by every signed-in role */}
            <Route path="handbook" element={<HandbookPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="settings" element={<SettingsPage />} />

            {/* Administration */}
            <Route path="admin" element={<StaffRoute />}>
              <Route index element={<AdminDashboardPage />} />
              <Route path="interns" element={<InternsPage />} />
              <Route path="interns/:userId" element={<InternDetailPage />} />

              <Route path="courses" element={<CourseManagerPage />} />
              <Route path="courses/:courseId" element={<CourseBuilderPage />} />
              <Route path="courses/:courseId/lessons/:lessonId" element={<LessonEditorPage />} />
              <Route path="learning-paths" element={<LearningPathsPage />} />
              <Route path="assessments" element={<AssessmentManagerPage />} />
              <Route path="skills" element={<SkillsAdminPage />} />

              <Route path="submissions" element={<ReviewQueuePage />} />
              <Route path="submissions/:submissionId" element={<ReviewDetailPage />} />
              <Route path="task-submissions" element={<TaskReviewQueuePage />} />
              <Route path="task-submissions/:submissionId" element={<TaskReviewDetailPage />} />
              <Route path="skips" element={<SkipRequestsPage />} />

              <Route path="reports" element={<ReportsPage />} />
              {/* Declared before the slug route so the hand-built skip report
                  keeps its own page rather than being read as a slug. */}
              <Route path="reports/skips" element={<SkipReportPage />} />
              <Route path="reports/:slug" element={<ReportViewerPage />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="activity" element={<ActivityLogPage />} />
              <Route path="audit-logs" element={<AuditLogPage />} />

              <Route path="projects" element={<ProjectsAdminPage />} />
              <Route path="projects/:projectId" element={<ProjectDetailPage />} />
              <Route path="tasks" element={<TasksAdminPage />} />

              <Route path="clients" element={<ClientsPage />} />
              <Route path="clients/:clientId" element={<ClientDetailPage />} />
              <Route path="finance" element={<FinanceOverviewPage />} />
              <Route path="finance/invoices" element={<InvoicesPage />} />
              <Route path="finance/invoices/:invoiceId" element={<InvoiceDetailPage />} />
              <Route path="finance/accountant" element={<AccountantDashboardPage />} />

              <Route path="users" element={<UsersPage />} />
              <Route path="settings" element={<PlatformSettingsPage />} />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
